import { createClient, AnamEvent } from '@anam-ai/js-sdk';
import type { AnamClient, ClientToolEvent } from '@anam-ai/js-sdk';
import {
  renderFlights,
  renderResorts,
  renderDynamicFlights,
  renderDynamicResorts,
  renderResortDetail,
  getStoredResortTagline,
  getStoredDestination,
  setOnResortSelect,
  setOnCabinSelect,
  showCheckout,
  hideCheckout,
  setOnPaymentComplete,
  setOnCheckoutClose
} from './ui';
import type { BookingData } from './ui';
import { MOCK_FLIGHTS, MOCK_RESORTS } from './data';
import type { ShowFlightsData, ShowResortsData } from './data';

// DOM Elements - Entry Screen
const entryScreen = document.getElementById('entry-screen')!;
const passwordForm = document.getElementById('password-form')!;
const passwordInput = document.getElementById('password-input') as HTMLInputElement;
const passwordError = document.getElementById('password-error')!;
const appMain = document.getElementById('app')!;

// DOM Elements - App
const avatarVideo = document.getElementById('avatar-video') as HTMLVideoElement;
const flightsPanel = document.getElementById('flights-panel')!;
const resortsPanel = document.getElementById('resorts-panel')!;
const statusEl = document.getElementById('status')!;

// Auth endpoint (Pages Function - same origin, no CORS)
const AUTH_URL = '/auth';

let client: AnamClient | null = null;

// ============================================
// Entry Screen - Password Gate
// ============================================
passwordForm.addEventListener('submit', async (e) => {
  e.preventDefault();

  const password = passwordInput.value.trim();
  if (!password) return;

  // Show loading state
  passwordForm.classList.add('loading');
  passwordError.hidden = true;

  try {
    // Call auth endpoint to validate password and get API key
    const response = await fetch(AUTH_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password })
    });

    if (!response.ok) {
      throw new Error('Invalid password');
    }

    const { sessionToken } = await response.json();

    // Password correct! Start the show
    entryScreen.classList.add('opening');

    setTimeout(() => {
      entryScreen.classList.add('hidden');
      appMain.removeAttribute('aria-hidden');

      // Connect to Anam with the session token (API key never exposed)
      connectToAnam(sessionToken);
    }, 800);

  } catch (error) {
    // Show error
    passwordForm.classList.remove('loading');
    passwordError.hidden = false;
    passwordInput.value = '';
    passwordInput.focus();
  }
});

// Close panels
document.querySelectorAll('[data-close]').forEach(btn => {
  btn.addEventListener('click', (e) => {
    const target = (e.currentTarget as HTMLElement).dataset.close;
    if (target === 'flights') flightsPanel.classList.remove('visible');
    if (target === 'resorts') resortsPanel.classList.remove('visible');
  });
});

// Connect to Anam with session token (API key never reaches browser)
async function connectToAnam(sessionToken: string) {
  setStatus('Connecting...');

  try {
    // Create client with session token
    client = createClient(sessionToken);

    // Log ALL events to debug
    console.log('Available AnamEvents:', Object.keys(AnamEvent));

    // Listen for ALL events to see what comes through
    Object.values(AnamEvent).forEach(eventName => {
      client!.addListener(eventName as any, (...args: any[]) => {
        console.log(`[AnamEvent] ${eventName}:`, args);
      });
    });

    // Handle client tool events
    client.addListener(AnamEvent.CLIENT_TOOL_EVENT_RECEIVED, handleToolEvent);

    // Connection status
    client.addListener(AnamEvent.CONNECTION_ESTABLISHED, () => {
      setStatus('Connected');
    });

    client.addListener(AnamEvent.CONNECTION_CLOSED, () => {
      setStatus('Disconnected');
    });

    // Start streaming to video element
    await client.streamToVideoElement('avatar-video');

  } catch (error) {
    console.error('Connection error:', error);
    setStatus('Connection failed');
  }
}

// Tool event handler - Progressive Disclosure
// Maya presents options, we render what she describes
function handleToolEvent(event: ClientToolEvent) {
  console.log('Tool event received:', event);

  switch (event.eventName) {
    // New progressive disclosure tools
    case 'show_flights':
      // Maya passes flight details from conversation
      if (event.eventData && Object.keys(event.eventData).length > 0) {
        renderDynamicFlights(event.eventData as ShowFlightsData);
      } else {
        renderFlights(MOCK_FLIGHTS, {});
      }
      flightsPanel.classList.add('visible');
      break;

    case 'show_resorts':
      // Maya passes resort details from conversation
      if (event.eventData && Object.keys(event.eventData).length > 0) {
        renderDynamicResorts(event.eventData as ShowResortsData);
      } else {
        renderResorts(MOCK_RESORTS, {});
      }
      resortsPanel.classList.add('visible');
      break;

    // Legacy tool names (backward compatibility)
    case 'search_flights':
      if (event.eventData && Object.keys(event.eventData).length > 0) {
        renderDynamicFlights(event.eventData as ShowFlightsData);
      } else {
        renderFlights(MOCK_FLIGHTS, {});
      }
      flightsPanel.classList.add('visible');
      break;

    case 'search_resorts':
      if (event.eventData && Object.keys(event.eventData).length > 0) {
        renderDynamicResorts(event.eventData as ShowResortsData);
      } else {
        renderResorts(MOCK_RESORTS, {});
      }
      resortsPanel.classList.add('visible');
      break;

    case 'hide_overlays':
      flightsPanel.classList.remove('visible');
      resortsPanel.classList.remove('visible');
      break;

    case 'initiate_booking':
      // Maya has received verbal agreement - show checkout
      flightsPanel.classList.remove('visible');
      resortsPanel.classList.remove('visible');
      if (event.eventData && Object.keys(event.eventData).length > 0) {
        showCheckout(event.eventData as BookingData);
      }
      break;

    default:
      console.warn('Unknown tool event:', event.eventName);
  }
}

// Status helper
function setStatus(text: string) {
  statusEl.textContent = text;
  statusEl.className = `status ${text.toLowerCase().replace(' ', '-')}`;
}

// Set up resort selection callback
setOnResortSelect((resortName) => {
  // Show detail view (zoom in on the resort)
  const tagline = getStoredResortTagline(resortName);
  const destination = getStoredDestination();
  renderResortDetail(resortName, destination, tagline);

  // Also tell Maya about the selection
  if (client && client.isStreaming()) {
    client.sendUserMessage(`Tell me more about ${resortName}`);
  } else {
    console.log('Resort selected (no active session):', resortName);
  }
});

// Set up cabin class selection callback
setOnCabinSelect((cabinClass) => {
  // Tell Maya the customer clicked on a cabin - conversational, not asking for lecture
  if (client && client.isStreaming()) {
    if (cabinClass === 'Upper Class') {
      client.sendUserMessage(`I'm looking at Upper Class`);
    } else if (cabinClass === 'Premium') {
      client.sendUserMessage(`I'm considering Premium`);
    } else {
      client.sendUserMessage(`Economy looks good`);
    }
  } else {
    console.log('Cabin selected (no active session):', cabinClass);
  }
});

// Set up payment complete callback - notify Maya
setOnPaymentComplete((booking) => {
  if (client && client.isStreaming()) {
    client.sendUserMessage(`I've just completed the booking for ${booking.resort_name} in ${booking.destination}!`);
  }
  console.log('Booking completed:', booking);
});

// Set up checkout close callback
setOnCheckoutClose(() => {
  console.log('Checkout closed');
});

// Debug: Keyboard shortcuts to test UI
// Press 1 = show flights, 2 = show resorts, 3 = show checkout, 0 = hide all
document.addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement) return; // ignore when typing

  switch (e.key) {
    case '1':
      // Test new flight ticket design with sample data
      renderDynamicFlights({
        route: 'London to Barbados',
        outbound_date: 'Saturday 15 February',
        return_date: 'Saturday 22 February',
        flights: [
          { departure_time: '09:15', arrival_time: '13:45', price_from: '£649', cabin_class: 'Economy' },
          { departure_time: '09:15', arrival_time: '13:45', price_from: '£1,249', cabin_class: 'Premium' },
          { departure_time: '09:15', arrival_time: '13:45', price_from: '£2,899', cabin_class: 'Upper Class' }
        ]
      });
      flightsPanel.classList.add('visible');
      break;
    case '2':
      // Test dynamic resorts with sample data
      renderDynamicResorts({
        destination: 'Barbados',
        resorts: [
          { name: 'Sandy Lane', tagline: 'The ultimate luxury escape', highlight: false },
          { name: 'Cobblers Cove', tagline: 'Intimate boutique charm', highlight: true },
          { name: 'The Crane Resort', tagline: 'Historic cliff-top elegance' }
        ]
      });
      resortsPanel.classList.add('visible');
      break;
    case '3':
      // Test checkout with sample booking
      flightsPanel.classList.remove('visible');
      resortsPanel.classList.remove('visible');
      showCheckout({
        destination: 'Barbados',
        resort_name: 'Cobblers Cove',
        resort_nights: 7,
        room_type: 'Ocean View Suite',
        origin_code: 'LHR',
        origin_city: 'London Heathrow',
        destination_code: 'BGI',
        departure_time: '09:15',
        arrival_time: '13:45',
        outbound_date: 'Saturday 15 February',
        return_date: 'Saturday 22 February',
        cabin_class: 'Premium',
        guests: 2,
        total_price: 5498,
        price_per_person: 2749,
        inclusions: [
          'Return flights in Premium',
          '7 nights at Cobblers Cove',
          'Ocean View Suite',
          'Airport transfers',
          'ATOL protection',
          '24/7 Virgin support'
        ]
      });
      break;
    case '0':
      flightsPanel.classList.remove('visible');
      resortsPanel.classList.remove('visible');
      hideCheckout();
      break;
  }
});
